import { ChevronDown, Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Skill, SkillCategory } from '@/types'
import type { SkillsViewMode } from './SkillsToolbar'
import { SkillCard } from './SkillCard'
import { SkillRow } from './SkillRow'

const dotColors: Record<SkillCategory, string> = {
  System: 'bg-info-foreground',
  Custom: 'bg-success-foreground',
}

const descriptions: Record<SkillCategory, string> = {
  System: 'Curated by QIVA and shared with every project. Duplicate one to adapt it.',
  Custom: 'Created and maintained by your team for this project.',
}

interface SkillCategorySectionProps {
  projectId: string
  category: SkillCategory
  skills: Skill[]
  viewMode: SkillsViewMode
  isExpanded: boolean
  onToggle: () => void
  emptyText: string
}

export function SkillCategorySection({
  projectId,
  category,
  skills,
  viewMode,
  isExpanded,
  onToggle,
  emptyText,
}: SkillCategorySectionProps) {
  const bodyId = `skills-section-body-${category}`

  return (
    <section data-testid={`skills-section-${category}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted"
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          data-testid={`skills-section-header-${category}`}
        >
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-foreground transition-transform duration-200', isExpanded ? 'rotate-0' : '-rotate-90')}
          />
          <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', dotColors[category])} />
          <span className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-foreground">{category}</span>
          <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-button px-1 text-[10px] font-semibold text-white">
            {skills.length}
          </span>
        </button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="rounded-full p-1 text-info-foreground hover:bg-muted" aria-label={`About ${category} skills`}>
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p>{descriptions[category]}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {isExpanded && (
        <div id={bodyId} className="mt-2">
          {skills.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {skills.map(skill => <SkillCard key={skill.id} projectId={projectId} skill={skill} />)}
            </div>
          ) : (
            <div className="space-y-3">
              {skills.map(skill => <SkillRow key={skill.id} projectId={projectId} skill={skill} />)}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
